import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { Project, CreateProjectData, PROJECT_STATUS_OPTIONS, PROJECT_PRIORITY_OPTIONS, ProjectDocument } from '../../types/project';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCustomers } from '../../hooks/useCustomers';
import { useUsers } from '../../hooks/useUsers';
import SearchableSelect from '../common/SearchableSelect';
import DocumentUpload from '../common/DocumentUpload';
import RichTextEditor from '../common/RichTextEditor';
import toast from 'react-hot-toast';
import { formatDateTimeForInput } from '../../utils/dateHelpers';
import {
  ExclamationTriangleIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  FolderIcon,
  PlusIcon,
  XMarkIcon,
  TagIcon,
  UserGroupIcon,
  ChatBubbleLeftEllipsisIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';


interface ProjectFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateProjectData) => Promise<void>;
  initialData?: Project;
}

const ProjectForm: React.FC<ProjectFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { customers } = useCustomers();
  const { users } = useUsers();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [newTag, setNewTag] = useState('');

  const [formData, setFormData] = useState<CreateProjectData>({
    name: '',
    key: '',
    description: '',
    status: 'planning',
    priority: 'medium',
    startDate: new Date(),
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    customerId: '',
    customerName: '',
    assignedUsers: [],
    assignedUsersNames: [],
    visibility: 'team',
    progress: 0,
    budget: 0,
    tags: [],
    notes: '',
    documents: [],
    organizationId: '',
    color: '#9333EA', // Alapértelmezett lila
    icon: '📁' // Alapértelmezett mappa emoji
  });

  // Form inicializálása
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          name: initialData.name,
          key: initialData.key,
          description: initialData.description,
          status: initialData.status,
          priority: initialData.priority,
          startDate: initialData.startDate && !isNaN(initialData.startDate.getTime()) ? initialData.startDate : new Date(''),
          endDate: initialData.endDate && !isNaN(initialData.endDate.getTime()) ? initialData.endDate : new Date(''),
          customerId: initialData.customerId,
          customerName: initialData.customerName,
          assignedUsers: initialData.assignedUsers,
          assignedUsersNames: initialData.assignedUsersNames,
          visibility: initialData.visibility,
          progress: initialData.progress,
          budget: initialData.budget || 0,
          tags: initialData.tags || [],
          notes: initialData.notes || '',
          documents: initialData.documents || [],
          organizationId: initialData.organizationId,
          color: initialData.color || '#9333EA',
          icon: initialData.icon || '📁'
        });
      } else {
        // Új projekt alapértelmezett értékei
        setFormData({
          name: '',
          key: '',
          description: '',
          status: 'planning',
          priority: 'medium',
          startDate: new Date(),
          endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          customerId: '',
          customerName: '',
          assignedUsers: user ? [user.id] : [],
          assignedUsersNames: user ? [user.displayName] : [],
          visibility: 'team',
          progress: 0,
          budget: 0,
          tags: [],
          notes: '',
          documents: [],
          organizationId: user?.organizationId || ''
        });
      }
      setError('');
    }
  }, [isOpen, initialData, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validáció
      if (!formData.name.trim()) {
        throw new Error(t('projects.validation.nameRequired') || 'Projekt neve kötelező');
      }
      
      if (!formData.description.trim()) {
        throw new Error(t('projects.validation.descriptionRequired') || 'Leírás kötelező');
      }

      // Dátumok érvényességének ellenőrzése
      if (!formData.startDate || isNaN(formData.startDate.getTime())) {
        throw new Error(t('projects.validation.startDateRequired') || 'Kezdési dátum megadása kötelező');
      }
      
      if (!formData.endDate || isNaN(formData.endDate.getTime())) {
        throw new Error(t('projects.validation.endDateRequired') || 'Befejezési dátum megadása kötelező');
      }

      if (formData.startDate >= formData.endDate) {
        throw new Error(t('projects.validation.dateRange') || 'A befejezés dátuma nem lehet korábbi a kezdés dátumánál');
      }

      await onSubmit(formData);
      
      // Sikeres mentés toast
      if (initialData) {
        toast.success(t('projects.messages.updateSuccess') || 'Projekt sikeresen frissítve!');
      } else {
        toast.success(t('projects.messages.createSuccess') || 'Projekt sikeresen létrehozva!');
      }
      
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    setFormData(prev => ({
      ...prev,
      customerId,
      customerName: customer 
        ? (customer.type === 'organization' 
            ? customer.companyName || ''
            : `${customer.lastName} ${customer.firstName}`)
        : ''
    }));
  };

  const handleAssignedUsersChange = (userIds: string[]) => {
    const userNames = userIds.map(id => {
      const user = users.find(u => u.id === id);
      return user ? user.displayName : '';
    }).filter(name => name !== '');

    setFormData(prev => ({
      ...prev,
      assignedUsers: userIds,
      assignedUsersNames: userNames
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Ügyfél opciók
  const customerOptions = customers.map(customer => ({
    value: customer.id,
    label: customer.type === 'organization' 
      ? customer.companyName || ''
      : `${customer.lastName} ${customer.firstName} (${customer.email})`
  }));

  // Felhasználó opciók
  const userOptions = users.map(user => ({
    value: user.id,
    label: `${user.displayName} (${user.email})`
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? t('projects.edit') || 'Projekt szerkesztése' : t('projects.new') || 'Új projekt'}
      size="6xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6 p-6">
        {/* Hiba üzenet */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 flex items-center space-x-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
          </div>
        )}

        {/* Projekt alapadatok szekció */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <FolderIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {t('projects.form.basicInfo') || 'Alapadatok'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.name') || 'Projekt neve'} <span className="text-red-500 dark:text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('projects.placeholders.name') || 'Adja meg a projekt nevét'}
                    required
                  />
                </div>

                {/* Projekt Szín */}
                <div>
                  <label htmlFor="color" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Projekt Szín
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      id="color"
                      value={formData.color || '#9333EA'}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="h-10 w-20 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {formData.color || '#9333EA'}
                    </span>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.description') || 'Leírás'} <span className="text-red-500 dark:text-red-400">*</span>
                  </label>
                  <RichTextEditor
                    id="description"
                    value={formData.description}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    placeholder={t('projects.placeholders.description') || 'Adja meg a projekt részletes leírását. Használhat markdown formázást, linkeket, listákat...'}
                    rows={6}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.status') || 'Státusz'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PROJECT_STATUS_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, status: option.value }))}
                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          formData.status === option.value
                            ? (() => {
                                const isDark = document.documentElement.classList.contains('dark');
                                switch (option.color) {
                                  case 'gray': return isDark ? 'bg-gray-600 text-gray-100' : 'bg-gray-200 text-gray-800';
                                  case 'blue': return isDark ? 'bg-blue-600 text-blue-100' : 'bg-blue-200 text-blue-800';
                                  case 'green': return isDark ? 'bg-green-600 text-green-100' : 'bg-green-200 text-green-800';
                                  case 'yellow': return isDark ? 'bg-yellow-600 text-yellow-100' : 'bg-yellow-200 text-yellow-800';
                                  case 'red': return isDark ? 'bg-red-600 text-red-100' : 'bg-red-200 text-red-800';
                                  default: return isDark ? 'bg-gray-600 text-gray-100' : 'bg-gray-200 text-gray-800';
                                }
                              })()
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.priority') || 'Prioritás'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PROJECT_PRIORITY_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, priority: option.value }))}
                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          formData.priority === option.value
                            ? (() => {
                                const isDark = document.documentElement.classList.contains('dark');
                                switch (option.color) {
                                  case 'green': return isDark ? 'bg-green-600 text-green-100' : 'bg-green-200 text-green-800';
                                  case 'yellow': return isDark ? 'bg-yellow-600 text-yellow-100' : 'bg-yellow-200 text-yellow-800';
                                  case 'orange': return isDark ? 'bg-orange-600 text-orange-100' : 'bg-orange-200 text-orange-800';
                                  case 'red': return isDark ? 'bg-red-600 text-red-100' : 'bg-red-200 text-red-800';
                                  default: return isDark ? 'bg-gray-600 text-gray-100' : 'bg-gray-200 text-gray-800';
                                }
                              })()
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Időzítés és költségvetés szekció */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-green-100 to-teal-100 dark:from-green-900/20 dark:to-teal-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <CalendarIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {t('projects.form.timing') || 'Időzítés és költségvetés'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.startDate') || 'Kezdés dátuma'}
                  </label>
                  <input
                    type="datetime-local"
                    id="startDate"
                    value={formatDateTimeForInput(formData.startDate)}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      if (!isNaN(newDate.getTime())) {
                        setFormData(prev => ({ ...prev, startDate: newDate }));
                      }
                    }}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    style={{
                      colorScheme: document.documentElement.classList.contains('dark') ? 'dark' : 'light'
                    }}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.endDate') || 'Befejezés dátuma'}
                  </label>
                  <input
                    type="datetime-local"
                    id="endDate"
                    value={formatDateTimeForInput(formData.endDate)}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      if (!isNaN(newDate.getTime())) {
                        setFormData(prev => ({ ...prev, endDate: newDate }));
                      }
                    }}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    style={{
                      colorScheme: document.documentElement.classList.contains('dark') ? 'dark' : 'light'
                    }}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.budget') || 'Költségvetés'}
                  </label>
                  <input
                    type="number"
                    id="budget"
                    value={formData.budget}
                    onChange={(e) => setFormData(prev => ({ ...prev, budget: Number(e.target.value) }))}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('projects.placeholders.budget') || 'Adja meg a költségvetést'}
                    min="0"
                    step="1000"
                  />
                </div>

                {/* Haladás automatikusan számolódik a taskok alapján */}
              </div>
            </div>
          </div>
        </div>

        {/* Ügyfél és csapat szekció */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <UserGroupIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {t('projects.form.teamAndClient') || 'Ügyfél és csapat'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="customer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.customer') || 'Ügyfél'}
                  </label>
                  <SearchableSelect
                    options={customerOptions}
                    value={formData.customerId}
                    onChange={handleCustomerChange}
                    placeholder={t('projects.placeholders.customer') || 'Válasszon ügyfelet'}
                  />
                </div>

                <div>
                  <label htmlFor="assignedUsers" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.assignedUsers') || 'Hozzárendelt felhasználók'}
                  </label>
                  <SearchableSelect
                    options={userOptions.filter(option => !formData.assignedUsers.includes(option.value))}
                    value=""
                    onChange={(userId) => {
                      if (userId && !formData.assignedUsers.includes(userId)) {
                        const user = users.find(u => u.id === userId);
                        setFormData(prev => ({
                          ...prev,
                          assignedUsers: [...prev.assignedUsers, userId],
                          assignedUsersNames: [...(prev.assignedUsersNames || []), user?.displayName || '']
                        }));
                      }
                    }}
                    placeholder={t('projects.placeholders.assignedUsers') || 'Válasszon felhasználókat'}
                  />
                </div>
              </div>

              {/* Kijelölt felhasználók megjelenítése */}
              {formData.assignedUsers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.selectedUsers') || 'Kijelölt felhasználók'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {formData.assignedUsers.map((userId, index) => {
                      const user = users.find(u => u.id === userId);
                      return (
                        <span
                          key={userId}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300"
                        >
                                                     {user?.displayName || formData.assignedUsersNames?.[index] || 'Ismeretlen'}
                          <button
                            type="button"
                            onClick={() => handleAssignedUsersChange(formData.assignedUsers.filter(id => id !== userId))}
                            className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Címkék és megjegyzések szekció */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <TagIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {t('projects.form.tagsAndNotes') || 'Címkék és megjegyzések'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.tags') || 'Címkék'}
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      id="tags"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="flex-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder={t('projects.placeholders.tags') || 'Új címke hozzáadása'}
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="px-3 py-2 bg-primary-600 dark:bg-primary-700 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Meglévő címkék */}
                  {formData.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="ml-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('projects.fields.notes') || 'Megjegyzések'}
                  </label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('projects.placeholders.notes') || 'Adja meg a megjegyzéseket'}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dokumentumok feltöltése */}
        <DocumentUpload
          documents={formData.documents}
          onDocumentsChange={(documents) => setFormData(prev => ({ ...prev, documents }))}
          uploadPath={`projects/${initialData?.id || 'temp'}/documents`}
          title={t('projects.fields.documents') || 'Dokumentumok'}
          subtitle={t('projects.placeholders.documents') || 'Csatolja a projekthez kapcsolódó dokumentumokat'}
          disabled={isSubmitting}
        />

        {/* Műveletek */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {t('common.cancel') || 'Mégse'}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-700 border border-transparent rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isSubmitting 
              ? (t('common.saving') || 'Mentés...')
              : (initialData ? (t('common.update') || 'Frissítés') : (t('common.create') || 'Létrehozás'))
            }
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ProjectForm; 